var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Class containing available configuration options for chart contents
 *
 * @class ChartContentConfig
 *
 * @esee http://documentation.chartiq.com/CIQ.ChartEngine.html#main
 */
import { defaultXAxisConfig, defaultYAxisConfig } from './utils/axisConfiguration';
var ChartContentConfig = /** @class */ (function () {
    function ChartContentConfig() {
        /**
         * User profile, basic, advanced or chartist
         *
         * basic: minimum functionalities in terms of studies, chart types and drawings
         *
         * advanced: medium functionalities
         *
         * chartist: all chart iq functionalities
         *
         * @default basic
         *
         * @type {string}
         */
        this.userProfile = 'basic';
        /**
         * If set to false, span will be kept on changing symbol, if true, span will be reseted to the one configured, if any
         *
         * @default true
         * @type {boolean}
         */
        this.resetSpan = false;
        /**
         * Sets the base chart type for the primary symbol.
         *
         * @default mountain
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#setChartType
         */
        this.chartType = 'mountain';
        /**
         * Draw a horizontal line at the current price. Only drawn if the most recent tick is visible
         *
         * @default true
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#preferences
         */
        this.currentPriceLine = true;
        /**
         * Set to true to display labels on y-axis for line based studies
         *
         * @default true
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#preferences
         */
        this.labels = true;
        /**
         * Set to true to disable automated price updates
         *
         * @default false
         */
        this.manualPriceUpdates = false;
        /**
         * Initial whitespace on right of the screen in pixels
         *
         * @default 0
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#preferences
         */
        this.whitespace = 0;
        /**
         * Amount of time represented by one bar or point or candle
         *
         * Number of intervals (if the interval is time unit) or interval-timeUnits (if the interval is numeric) from the source data to roll up
         * into one visual chart bar.
         *
         * @default 1
         */
        this.periodicity = 1;
        /**
         * When containing a numeric value, specifies the number of timeUnits in a bar.
         * For example, a 5 minute bar would have an interval of 5 and a timeUnit of 'minute'.
         * If interval is not numeric, it represents the time unit, which could be 'day', 'week', 'month' or 'tick'.
         *
         * @default 5
         */
        this.interval = 1;
        /**
         * is the time unit of the bars in the source data when the interval is numeric
         *
         * @default day
         */
        this.timeUnit = 'day';
        /**
         * Sets the chart to display the requested time span.
         *
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#setSpan
         *
         * @default 1
         */
        this.spanMultiplier = 1;
        /**
         *
         * @default year
         *
         * @type {string}
         */
        this.spanBase = 'year';
        /**
         * Sets a chart to the requested date range.
         *
         * If you set the range via layout it will take precedence over span
         *
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#setRange
         */
        this.rangeFrom = null;
        this.rangeTo = null;
        /**
         * Config of xAxis refer XAxisConfig for using (Ctrl+Click)
         *
         * @link https://documentation.chartiq.com/CIQ.ChartEngine.YAxis.htm
         */
        this.xAxis = __assign({}, defaultXAxisConfig);
        /**
         * Config of xAxis refer YAxisConfig for using (Ctrl+Click)
         *
         * @link https://documentation.chartiq.com/CIQ.ChartEngine.XAxis.html
         */
        this.yAxis = __assign({}, defaultYAxisConfig);
        /**
         * Disable stickies (Tooltip Element for showing Trash Can button/ Series delete panel
         *
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#htmlControls%5B%60mSticky%60%5D
         */
        this.mSticky = null;
        this.drawOk = null;
        /**
         * Set to zero to avoid resize checking loop
         *
         * @see http://documentation.chartiq.com/CIQ.ChartEngine.html#resizeDetectMS
         */
        this.resizeDetectMS = 0;
        /**
         * Sets this config option to true if you want the chart not to calculate the best periodicity for the current range
         *
         * (This helps when using 'today' with 1 minute and you want to push data)
         *
         * @default false
         *
         * @type {boolean}
         */
        this.maintainPeriodicity = false;
        /**
         * Ranges to be shown in the ranges bar at the bottom of the chart
         *
         * multiplier   The period that will be passed to {@link CIQ.ChartEngine#setSpan}
         *
         * base The interval that will be passed to {@link CIQ.ChartEngine#setSpan}
         *
         * [interval] Optional chart interval to use (leave empty for autodetect)
         *
         * [period] Optional chart period to use (leave empty for autodetect)
         *
         * [hideSm] if true, the range will not be shown on sm size, it is recommended to show a maximum of 5 ranges on sm size
         *
         * @type: {label: string, multiplier: string | number, base: string, interval?: string, period?: string, hideSm?: boolean}[]
         */
        this.showRanges = [
            {
                label: '1 d',
                multiplier: 1,
                base: 'today',
                interval: '1',
                period: '1',
            },
            {
                label: '5 d',
                multiplier: 5,
                base: 'day',
                interval: '1',
                period: '5',
            },
            {
                label: '1 m',
                multiplier: 1,
                base: 'month',
                interval: 'day',
                period: '1',
                hideSm: true,
            },
            {
                label: '3 m',
                multiplier: 3,
                base: 'month',
                interval: 'day',
                period: '1',
                hideSm: true,
            },
            {
                label: '6 m',
                multiplier: 6,
                base: 'month',
                interval: 'day',
                period: '1',
                hideSm: true,
            },
            {
                label: 'ytd',
                multiplier: 1,
                base: 'ytd',
                interval: 'day',
                period: '1',
            },
            {
                label: '1 y',
                multiplier: 1,
                base: 'year',
                interval: 'day',
                period: '1',
            },
            {
                label: '5 y',
                multiplier: 5,
                base: 'year',
                hideSm: true,
            },
            {
                label: 'all',
                multiplier: 1,
                base: 'all',
                hideSm: true,
            },
        ];
        /**
         * If true, sharing button will be shown and export of chart as PNG would be active
         */
        this.export = false;
        /**
         * Default options used to setup continuous zoom
         *
         * @see https://documentation.chartiq.com/CIQ.ContinuousZoom.html#main
         */
        this.continuousZoomDefaultOptions = {
            periodicities: [
                { period: 1, interval: 'month' },
                { period: 1, interval: 'day' },
                { period: 2, interval: 30 },
                { period: 1, interval: 5 },
                { period: 1, interval: 1 },
            ],
            boundaries: {
                maxCandleWidth: 100,
                minCandleWidth: 3,
                maxTicks: 500,
                minTicks: 15,
            },
        };
        /**
         * If set to true a suggested legend renderer will be loaded
         *
         * Alternatively you can set your own renderer using setLegendRenderer
         * @optional
         */
        this.useSuggestedLegendRenderer = false;
        /**
         * Set this to false in order to stop the library
         * to save the layout
         *
         * (only used by chart engine at the moment)
         * @optional
         */
        this.saveLayout = true;
        /**
         * Set to true if you'd like your drawings to be stored in local storage
         *
         * (only used by chart-content at the moment)
         * @optional
         */
        this.useLocalStorageDrawings = false;
        /**
         * Set to true if you want to show the previous day on intraday data
         * if no data is yet available due to delayed quotes
         *
         * @optional
         */
        this.useDelayedQuotesFallback = false;
        /**
         * Set to true for creating missing data points during not trading hours
         *
         * @optional
         */
        this.useCleanupGaps = false;
    }
    return ChartContentConfig;
}());
export { ChartContentConfig };
//# sourceMappingURL=chart-content-config.js.map